import { useCallback } from 'react';
import axios from 'axios';
import { FileType, useGetFileSignedUrlsMutation } from '@/queries/file';
import { FileSignedUrl, TMDXFile, TMDXFile as UploadedFile } from '@/api/apis/files';

export type FileMeta = {
  fileName: string;
  fileContentLength: number;
  fileExtension: string;
};

const getMimeType = (fileExtension: string = ''): string => {
  const extension: string | undefined = fileExtension.split('.').pop();
  const mimeTypes: { [key: string]: string } = {
    pdf: 'application/pdf',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', //+
    doc: 'application/msword',
    mov: 'video/quicktime',
    mp4: 'video/mp4',
    hevc: 'video/mp4',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    heic: 'image/heic',
  };

  return mimeTypes[extension || ''] || '';
};

export const useUploadFile = (fileType: FileType) => {
  const { mutateAsync } = useGetFileSignedUrlsMutation();

  const getSignedUrls = useCallback(
    async (filesMeta: FileMeta[]) => {
      const signedUrlsResults = await mutateAsync({ files: filesMeta, fileType });

      return signedUrlsResults.reduce((acc, signedUrlsResult: PromiseSettledResult<FileSignedUrl>, index) => {
        if (signedUrlsResult.status === 'fulfilled') {
          const origFile = filesMeta[index];

          const item =
            fileType === FileType.Case || fileType === FileType.Offer
              ? {
                  fileId: signedUrlsResult.value.fileId,
                  signedUrl: signedUrlsResult.value.signedUrl,
                  fileName: signedUrlsResult.value.filePath!.split('/').pop(),
                  origFileName: origFile?.fileName,
                  filePath: signedUrlsResult.value.filePath,
                }
              : {
                  signedUrl: signedUrlsResult.value.signedUrl,
                  fileName: signedUrlsResult.value.fileName,
                  origFileName: origFile?.fileName,
                };

          return [...acc, item];
        }

        return acc;
      }, [] as FileSignedUrl[]);
    },
    [mutateAsync, fileType],
  );

  const uploadFiles = useCallback(
    async (files: TMDXFile[]): Promise<TMDXFile[] | []> => {
      const fileMeta = files.map((file) => {
        const regex = /\.([^.]+)$/;
        const match = file.fileName.match(regex);
        const extension = match?.[1]?.toLowerCase() || '';
        return {
          fileName: file.fileName,
          fileContentLength: file.file?.size || 0,
          fileExtension: `.${extension}`,
        };
      });
      const signedUrls = await getSignedUrls(fileMeta);

      if (signedUrls.length > 0) {
        const uploads = await Promise.allSettled(
          signedUrls.map(async (signedUrlFile) => {
            const file =
              fileType === FileType.Case || fileType === FileType.Offer
                ? files.find((f) => f.fileName === signedUrlFile.fileName)
                : files[0];
            const meta = fileMeta.find((f) => f.fileName === signedUrlFile.origFileName);

            await axios.put(signedUrlFile.signedUrl, file?.file, {
              headers: { 'Content-Type': getMimeType(meta?.fileExtension) },
            });

            return signedUrlFile;
          }),
        );
        const hasRejected = uploads.find((upload: PromiseSettledResult<FileSignedUrl>) => upload.status === 'rejected');

        if (hasRejected) {
          throw hasRejected.reason;
        }

        const uploadedFiles: UploadedFile[] = (
          uploads.filter(
            (upload: PromiseSettledResult<FileSignedUrl>) => upload.status === 'fulfilled',
          ) as PromiseFulfilledResult<FileSignedUrl>[]
        ).map((upload: PromiseFulfilledResult<FileSignedUrl>) => ({
          id: upload.value.fileId ? upload.value.fileId : undefined,
          fileName: upload.value!.fileName!,
          path: upload.value!.filePath!,
        }));

        return uploadedFiles;
      }
      return [];
    },
    [fileType, getSignedUrls],
  );

  return {
    uploadFiles,
  };
};
